import debugFn from 'debug';
import { EventEmitter } from 'events';
import { Milliseconds, Seconds } from '../../../../types/date';
import { SegmentData } from '../SegmentProvider';
import { PlaybackTech, SegmentWithAudio } from '../types';
import { IAudioEngine } from './IAudioEngine';

const debug = debugFn('audio:media-source-engine');
debug.color = '#ff7d55';

/**
 * Audio engine that uses the MediaSource API to play audio buffers.
 *
 * Enables gapless playback by appending multiple audio segments to a single
 * SourceBuffer. Time is tracked relative to the current segment, allowing
 * seamless transitions between segments without audio gaps.
 *
 * @fires timeupdate - Emits segment-relative offset in milliseconds
 * @fires segmentEnded - Current segment's audio has finished playing
 * @fires ended - All buffered audio has finished (no more segments)
 * @fires error - An error occurred during playback or buffer operations
 */
export class MediaSourceEngine extends EventEmitter implements IAudioEngine {
  private audio: HTMLAudioElement | null = null;
  private mediaSource: MediaSource | null = null;
  private sourceBuffer: SourceBuffer | null = null;
  private pendingBuffers: ArrayBuffer[] = [];
  private isProcessingQueue = false;
  private volume = 100;
  private muted = false;

  // Segment offset tracking - reports time relative to current segment
  // ┌────────────────────────────────────────────────────────────────────┐
  // │ The SourceBuffer holds a continuous timeline of all appended audio │
  // │ segments. To report time relative to the CURRENT segment (not the  │
  // │ absolute buffer position), we track where each segment starts.     │
  // │                                                                    │
  // │ Buffer timeline:  [  Segment A  ][  Segment B  ][  Segment C  ]    │
  // │ Absolute time:    0ms          100ms         250ms         400ms   │
  // │ segmentStartOffset for B = 100ms                                   │
  // │ If audio.currentTime = 150ms, offsetWithinSegment = 50ms           │
  // └────────────────────────────────────────────────────────────────────┘
  private segmentStartOffset = 0;
  private currentSegmentDuration = 0;
  private hasEmittedSegmentEnded = false;

  /**
   * Initializes the audio element, MediaSource, and SourceBuffer.
   * Must be called before any other method.
   */
  async init(): Promise<void> {
    debug('initializing MediaSourceEngine');

    this.audio = document.createElement('audio');
    this.audio.preload = 'auto';
    this.audio.className = 'audio-player-ms';
    this.audio.muted = this.muted;
    document.body.appendChild(this.audio);

    this.mediaSource = new MediaSource();

    // Create a blob URL that connects the audio element to the MediaSource.
    // This allows us to dynamically append audio data via the SourceBuffer.
    this.audio.src = URL.createObjectURL(this.mediaSource);

    // MediaSource starts in 'closed' state. We must wait for 'sourceopen'
    // before we can create a SourceBuffer and append data.
    await new Promise<void>((resolve, reject) => {
      this.mediaSource!.addEventListener('sourceopen', () => {
        try {
          debug('MediaSource opened, creating SourceBuffer');
          this.sourceBuffer = this.mediaSource!.addSourceBuffer('audio/mpeg');

          this.sourceBuffer.addEventListener('updateend', () => {
            this.onBufferUpdateEnd();
          });

          this.sourceBuffer.addEventListener('error', (e) => {
            debug('SourceBuffer error:', e);
            this.emit('error', new Error('SourceBuffer error'));
          });

          resolve();
        } catch (error) {
          reject(error);
        }
      });

      this.mediaSource!.addEventListener('error', (e) => {
        debug('MediaSource error:', e);
        reject(new Error('MediaSource initialization failed'));
      });
    });

    this.setupAudioEventListeners();

    debug('MediaSourceEngine initialized');
  }

  /** Attaches timeupdate, ended, and error listeners to the audio element. */
  private setupAudioEventListeners(): void {
    if (!this.audio) return;

    this.audio.addEventListener('timeupdate', () => {
      if (!this.audio) {
        console.warn('audio element not found when timeupdate event fired');
        return;
      }

      // Convert absolute buffer position to segment-relative offset.
      // Consumers expect time relative to the current segment, not the
      // accumulated buffer position.
      const currentTimeMs = this.audio.currentTime * 1000;
      const offsetWithinSegment = currentTimeMs - this.segmentStartOffset;
      this.emit('timeupdate', offsetWithinSegment);
      this.checkSegmentEnd(offsetWithinSegment);
    });

    this.audio.addEventListener('ended', () => this.emit('ended'));
    this.audio.addEventListener('error', (error) => {
      console.error('Audio element error', error);
    });
  }

  /** Starts or resumes audio playback. */
  async play(): Promise<void> {
    if (!this.audio) throw new Error('Engine not initialized');
    debug('play');
    await this.audio.play();
  }

  /** Pauses audio playback. */
  pause(): void {
    if (!this.audio) return;
    debug('pause');
    this.audio.pause();
  }

  /** Clears all buffered audio and resets playback position to the start. */
  async reset(): Promise<void> {
    debug('reset');

    this.pendingBuffers = [];
    this.isProcessingQueue = false;
    this.segmentStartOffset = 0;
    this.currentSegmentDuration = 0;
    this.hasEmittedSegmentEnded = false;

    if (
      this.sourceBuffer &&
      !this.sourceBuffer.updating &&
      this.mediaSource?.readyState === 'open'
    ) {
      try {
        // Clear the buffer
        const buffered = this.sourceBuffer.buffered;
        if (buffered.length > 0) {
          this.sourceBuffer.remove(0, buffered.end(buffered.length - 1));
          await this.waitForUpdateEnd();
        }
        // Reset timestampOffset so next append starts at position 0.
        // MP3 frames don't carry explicit timestamps - the SourceBuffer uses
        // timestampOffset to determine where to place incoming data. Without
        // this reset, new data would be placed after the cleared region.
        this.sourceBuffer.timestampOffset = 0;
      } catch (e) {
        debug('error clearing buffer:', e);
      }
    }

    if (this.audio) {
      this.audio.currentTime = 0;
    }
  }

  /**
   * Loads and buffers a segment as the current playback target.
   * Sets this segment as the active one for time tracking.
   */
  async enqueue(data: SegmentData): Promise<void> {
    const buffer = this.validateAndGetBuffer(data);

    const segmentDuration = data.segment.endTs - data.segment.startTs;
    debug('enqueue segment (duration: %dms)', segmentDuration);

    this.currentSegmentDuration = segmentDuration;
    this.hasEmittedSegmentEnded = false;

    await this.appendBufferAndWait(buffer);
  }

  /**
   * Pre-buffers the next segment for gapless playback.
   * Does not change the current segment tracking.
   */
  async enqueueNext(data: SegmentData): Promise<void> {
    const buffer = this.validateAndGetBuffer(data);
    debug('enqueue next segment');
    await this.appendBufferAndWait(buffer);
  }

  /** Validates segment has audio data and returns the ArrayBuffer for MSE append. */
  private validateAndGetBuffer(data: SegmentData): ArrayBuffer {
    if (!this.isSegmentWithAudio(data)) {
      throw new Error('MediaSourceEngine requires audio buffer data');
    }
    return data.audio;
  }

  /** Appends buffer to the SourceBuffer queue and waits for completion. */
  private async appendBufferAndWait(buffer: ArrayBuffer): Promise<void> {
    this.pendingBuffers.push(buffer);
    this.processQueue();
    await this.waitForUpdateEnd();
  }

  /**
   * Updates segment tracking to the next buffered segment.
   * Called when transitioning from one segment to the next.
   *
   * The audio continues playing seamlessly (gapless), but we need to update
   * our bookkeeping so timeupdate events report the correct segment-relative
   * offset for the NEW segment.
   *
   * @param nextSegmentDuration - Duration of the next segment in milliseconds
   */
  advanceToNextBuffer(nextSegmentDuration: number): void {
    debug(
      'advance to next buffer, updating offset from %d to %d',
      this.segmentStartOffset,
      this.segmentStartOffset + this.currentSegmentDuration,
    );

    // Move the "start of current segment" pointer forward by the previous
    // segment's duration. Now offsetWithinSegment calculations will be
    // relative to the new segment.
    this.segmentStartOffset += this.currentSegmentDuration;
    this.currentSegmentDuration = nextSegmentDuration;
    this.hasEmittedSegmentEnded = false;
  }

  /**
   * Seeks to a position within the current segment.
   * @param offsetMs - Offset relative to the current segment start
   * @returns The actual offset seeked to
   */
  async seekToOffset(offsetMs: Milliseconds): Promise<Milliseconds> {
    if (!this.audio) return 0 as Milliseconds;

    // Convert segment-relative offset to absolute buffer time
    const absoluteMs = this.segmentStartOffset + offsetMs;
    const seconds = (absoluteMs / 1000) as Seconds;
    debug('seek to offset %dms (absolute: %dms, %ds)', offsetMs, absoluteMs, seconds);

    // readyState < 1 means no metadata yet (HAVE_NOTHING state).
    // Setting currentTime before metadata is loaded will be ignored or error.
    // We wait for 'canplay' which fires when enough data is buffered to play.
    if (this.audio.readyState < 1) {
      await new Promise<void>((resolve) => {
        this.audio!.addEventListener(
          'canplay',
          () => {
            this.audio!.currentTime = seconds;
            resolve();
          },
          { once: true },
        );
      });
    } else {
      this.audio.currentTime = seconds;
    }

    // MediaSource has full buffer control, so we always return the requested offset
    return offsetMs;
  }

  /**
   * Sets the playback volume.
   * @param value - Volume level from 0 to 100
   */
  setVolume(value: number): void {
    this.volume = value;
    if (this.audio) {
      this.audio.volume = value / 100;
    }
  }

  /** Returns the current volume level (0-100). */
  getVolume(): number {
    return this.volume;
  }

  /** Mutes the audio element. */
  mute(): void {
    this.muted = true;
    if (this.audio) {
      this.audio.muted = true;
    }
  }

  /** Unmutes the audio element. */
  unmute(): void {
    this.muted = false;
    if (this.audio) {
      this.audio.muted = false;
    }
  }

  /** Check if the engine is muted. */
  isMuted(): boolean {
    return this.muted;
  }

  /** Returns the playback technology this engine uses. */
  getTech(): PlaybackTech {
    return PlaybackTech.MEDIA_SOURCE;
  }

  /** Releases all resources: audio element, MediaSource, and event listeners. */
  dispose(): void {
    debug('dispose');

    this.pendingBuffers = [];
    this.isProcessingQueue = false;

    // 1. Clean up the Buffer FIRST while the MediaSource is still "live"
    if (this.sourceBuffer && this.mediaSource) {
      try {
        // Only abort if the MediaSource is still 'open'
        if (this.mediaSource.readyState === 'open') {
          this.sourceBuffer.abort();
          this.mediaSource.removeSourceBuffer(this.sourceBuffer);
        }
      } catch {
        // If it's already detached, we don't care.
        // The browser is already doing our job for us.
        debug('SourceBuffer already detached or invalid during dispose');
      }
    }

    // 2. Kill the Audio Element relationship
    if (this.audio) {
      this.audio.pause();

      const blobUrl = this.audio.src;
      this.audio.removeAttribute('src');
      this.audio.load(); // This is the final nail in the coffin

      if (blobUrl?.startsWith('blob:')) {
        try {
          window.URL.revokeObjectURL(blobUrl);
        } catch (e) {
          debug('error revoking URL:', e);
        }
      }

      if (this.audio.parentNode) {
        document.body.removeChild(this.audio);
      }
      this.audio = null;
    }

    this.sourceBuffer = null;
    this.mediaSource = null;
    this.removeAllListeners();
  }

  /** Emits 'segmentEnded' when playback reaches the current segment's end. */
  private checkSegmentEnd(offsetWithinSegment: number): void {
    if (this.hasEmittedSegmentEnded || this.currentSegmentDuration <= 0) {
      return;
    }

    // Emit segmentEnded when we've reached or passed the segment duration
    if (offsetWithinSegment >= this.currentSegmentDuration) {
      debug(
        'segment ended (offset: %dms, duration: %dms)',
        offsetWithinSegment,
        this.currentSegmentDuration,
      );
      this.hasEmittedSegmentEnded = true;
      this.emit('segmentEnded');
    }
  }

  /**
   * Processes pending buffers sequentially, appending them to the SourceBuffer.
   *
   * SourceBuffer only allows one append operation at a time. If we call
   * appendBuffer() while another is in progress, it throws an error.
   * This queue pattern ensures buffers are appended one at a time:
   *
   *   1. processQueue() appends one buffer and sets isProcessingQueue=true
   *   2. When append completes, 'updateend' fires → onBufferUpdateEnd()
   *   3. onBufferUpdateEnd() sets isProcessingQueue=false and calls processQueue()
   *   4. If more buffers exist, step 1 repeats
   */
  private processQueue(): void {
    if (this.isProcessingQueue || this.pendingBuffers.length === 0) {
      return;
    }

    if (!this.sourceBuffer || this.sourceBuffer.updating) {
      return;
    }

    const buffer = this.pendingBuffers.shift();
    if (!buffer) return;

    this.isProcessingQueue = true;

    try {
      debug('appending buffer (%d bytes)', buffer.byteLength);
      this.sourceBuffer.appendBuffer(buffer);
    } catch (error) {
      debug('error appending buffer:', error);
      this.isProcessingQueue = false;
      const message = error instanceof Error ? error.message : String(error);
      this.emit('error', new Error(`Failed to append buffer: ${message}`));
    }
  }

  /** Handler for SourceBuffer 'updateend' event; continues queue processing. */
  private onBufferUpdateEnd(): void {
    this.isProcessingQueue = false;
    this.processQueue();
  }

  /** Returns a promise that resolves when the SourceBuffer finishes updating. */
  private waitForUpdateEnd(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.sourceBuffer || !this.sourceBuffer.updating) {
        resolve();
        return;
      }
      this.sourceBuffer.addEventListener('updateend', () => resolve(), {
        once: true,
      });
    });
  }

  /** Type guard to check if segment data contains audio buffer. */
  private isSegmentWithAudio(data: SegmentData): data is SegmentWithAudio {
    return 'audio' in data && data.audio !== undefined;
  }
}
